import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Wallet } from "lucide-react";
import { api, MANUAL_PAYMENT } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isManual = params.get("manual") === "1";
  const orderId = params.get("oid");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const { clear } = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (isManual) return;
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setStatus("timeout");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) { clear(); clearedRef.current = true; }
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isManual]);

  if (isManual) {
    return (
      <>
        <Seo title="Order Placed | Just Peptides Supply" path="/checkout/success" noindex />
        <section className="mx-auto max-w-xl px-5 py-24 text-center">
          <div data-testid="status-manual" className="fade-up">
            <CheckCircle2 className="mx-auto h-16 w-16 text-brand-teal" />
            <h1 className="mt-6 font-display text-4xl text-brand-navy">Order placed</h1>
            <p className="mt-3 text-brand-navy/60">
              Thank you! Your order has been recorded. Please complete payment using one of the methods below to begin fulfillment.
            </p>
            {orderId && <p className="mt-2 font-mono-label text-xs uppercase tracking-[0.15em] text-brand-navy/40">Order #{orderId.slice(0, 8)}</p>}

            <div className="mt-8 rounded-2xl border border-brand-navy/10 bg-white p-6 text-left">
              <div className="flex items-center gap-2 mb-4">
                <Wallet className="h-5 w-5 text-brand-blue" />
                <h2 className="font-display text-lg text-brand-navy">Complete your payment</h2>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span className="text-brand-navy/55">Zelle</span><span className="font-semibold text-brand-navy">{MANUAL_PAYMENT.zelle}</span></div>
                <div className="flex justify-between gap-3"><span className="text-brand-navy/55">Venmo</span><span className="font-semibold text-brand-navy">{MANUAL_PAYMENT.venmo}</span></div>
                <div className="flex justify-between gap-3"><span className="text-brand-navy/55">Cash App</span><span className="font-semibold text-brand-navy">{MANUAL_PAYMENT.cashapp}</span></div>
                <div className="flex justify-between gap-3"><span className="text-brand-navy/55">Email</span><span className="font-semibold text-brand-navy break-all">{MANUAL_PAYMENT.email}</span></div>
              </div>
              <p className="mt-4 pt-4 border-t border-brand-navy/10 text-xs leading-relaxed text-brand-navy/60">{MANUAL_PAYMENT.note}</p>
              <p className="mt-2 text-xs leading-relaxed text-brand-navy/60">{MANUAL_PAYMENT.instagram}</p>
            </div>

            <div className="mt-8 flex justify-center gap-3">
              <Link to="/shop"><Button className="rounded-full bg-brand-navy hover:bg-brand-blue text-white px-7 h-12">Continue Shopping</Button></Link>
              <Link to="/account"><Button variant="outline" className="rounded-full h-12 px-7">My Orders</Button></Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo title="Order Status | Just Peptides Supply" path="/checkout/success" noindex />
      <section className="mx-auto max-w-xl px-5 py-32 text-center">
        {status === "checking" && (
          <div data-testid="status-checking">
            <Loader2 className="mx-auto h-14 w-14 text-brand-blue animate-spin" />
            <h1 className="mt-6 font-display text-3xl text-brand-navy">Confirming your payment…</h1>
            <p className="mt-3 text-brand-navy/60">Please wait a moment.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="status-paid" className="fade-up">
            <CheckCircle2 className="mx-auto h-16 w-16 text-brand-teal" />
            <h1 className="mt-6 font-display text-4xl text-brand-navy">Order confirmed</h1>
            <p className="mt-3 text-brand-navy/60">Thank you for your purchase. A confirmation has been recorded for your order.</p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/shop"><Button className="rounded-full bg-brand-navy hover:bg-brand-blue text-white px-7 h-12">Continue Shopping</Button></Link>
              <Link to="/account"><Button variant="outline" className="rounded-full h-12 px-7">My Orders</Button></Link>
            </div>
          </div>
        )}
        {(status === "failed" || status === "timeout") && (
          <div data-testid="status-failed">
            <XCircle className="mx-auto h-16 w-16 text-destructive" />
            <h1 className="mt-6 font-display text-3xl text-brand-navy">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="mt-3 text-brand-navy/60">
              {status === "timeout" ? "Your payment is taking longer than expected. Check your email for confirmation." : "Something went wrong. Please try again."}
            </p>
            <Link to="/cart"><Button className="mt-8 rounded-full bg-brand-navy hover:bg-brand-blue text-white px-7 h-12">Back to Cart</Button></Link>
          </div>
        )}
      </section>
    </>
  );
}
